import { useMutation } from '@apollo/client'

import {
  OrderReviewGigaQueryDocument,
  UpdateConnectionDocument,
  UpdateHeaderPricetypesAndDatesDocument,
  UpsertAccountCustomerCodesInput,
} from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

import {
  UseOrderDeliveryMutationReturnType,
  UseOrdersMutationReturnType,
  useOrderDeliveryMutation,
  useOrdersMutation,
} from 'features/Orders/orders.hooks'

const UPDATE_ORDER_INFO = atlas`#graphql
  mutation UpdateOrderInfoDupe3($input: UpdateOrderInfoInput) {
    updateOrderInfo(input: $input) {
      updatedOrderIds
    }
  }
`

atlas`#graphql
mutation UpdateHeaderPricetypesAndDates($input: UpdateOrdersConnection!) {
  updateOrders(input: $input) {
    orders {
      id
    }
    errors {
      message
    }
  }
}
`

export const useUpdateOrderInfo = (): UseOrdersMutationReturnType => {
  return useOrdersMutation(UPDATE_ORDER_INFO, {
    refetchQueries: [OrderReviewGigaQueryDocument],
  })
}

const UPDATE_ORDER_DELIVERY_INFO = atlas`#graphql
  mutation UpdateOrderDeliveryInfoDupe1($input: UpdateOrderDeliveryInfoInput!) {
    updateOrderDeliveryInfo(input: $input) {
      updatedOrderIds
    }
  }
`

export const useUpdateOrderNumber = (): UseOrderDeliveryMutationReturnType =>
  useOrderDeliveryMutation(UPDATE_ORDER_DELIVERY_INFO)

export const useUpdateRetailerCode = () => {
  const [mutate, { loading }] = useMutation(UpdateConnectionDocument, {
    client: atlasClient,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    update(cache, { data }) {
      const retailerId = toGlobalId(
        'Retailer',
        fromGlobalId(data?.upsertAccountCustomerCodes[0].otherAccountId).id,
      )
      cache.modify({
        id: `PublicRetailerProfile:${retailerId}`,
        fields: {
          code: function () {
            return data?.upsertAccountCustomerCodes[0].code || ''
          },
        },
      })
    },
  })

  const onUpdateRetailerCode = async (
    input: UpsertAccountCustomerCodesInput,
  ) => {
    if (!Boolean(input.customerCodes[0].otherAccountId)) return

    try {
      const response = await mutate({
        variables: {
          input,
        },
      })

      if (!!response.errors?.length) {
        return
      }

      return response.data?.upsertAccountCustomerCodes?.[0].code
    } catch (error) {
      return
    }
  }

  return {
    isUpdatingRetailerCode: loading,
    onUpdateRetailerCode,
  }
}

export const useUpdateHeaderDates = () => {
  const [mutate] = useMutation(UpdateHeaderPricetypesAndDatesDocument, {
    client: atlasClient,
  })

  const onUpdateHeaderDates = async (params: {
    id: string
    priceTypeId?: string
    deliveryWindowStart?: string
    deliveryWindowEnd?: string
  }) => {
    try {
      const response = await mutate({
        variables: {
          input: {
            orders: [
              {
                ...params,
              },
            ],
          },
        },
        refetchQueries: [OrderReviewGigaQueryDocument],
      })

      if (!!response.errors?.length) {
        return
      }

      return response.data
    } catch (error) {
      return
    }
  }

  return { onUpdateHeaderDates }
}
